const pdfContentType = "application/pdf";
const asyncRequestHeader = "X-Requested-With";
const asyncRequestValue = "XMLHttpRequest";

export interface DocumentPdfFile {
  content: Blob;
  fileName: string;
}

export interface DocumentPdfDownloadServices {
  document?: Document;
  request?: (url: string) => Promise<DocumentPdfFile>;
  save?: (file: DocumentPdfFile) => void;
}

export class DocumentPdfDownloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DocumentPdfDownloadError";
  }
}

export function initializeDocumentPdfDownloads(root: Document = document) {
  root.addEventListener("click", event => {
    const target = event.target as Element | null;
    const trigger = target?.closest<HTMLButtonElement>("[data-document-pdf-download]");
    if (!trigger || trigger.disabled) return;

    const url = trigger.dataset.pdfDownloadUrl;
    if (!url) return;

    void runDocumentPdfDownload(trigger, url);
  });
}

export async function requestDocumentPdf(
  url: string,
  fetcher: typeof fetch = globalThis.fetch.bind(globalThis)): Promise<DocumentPdfFile> {
  let response: Response;
  try {
    response = await fetcher(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        "Accept": pdfContentType,
        [asyncRequestHeader]: asyncRequestValue
      }
    });
  } catch {
    throw new DocumentPdfDownloadError("The PDF request failed. Check the connection and try again.");
  }

  if (response.status === 401 || response.status === 403 || redirectedToLogin(response)) {
    throw new DocumentPdfDownloadError("Your session expired. Sign in again, then retry.");
  }

  if (response.status === 503) {
    throw new DocumentPdfDownloadError("The PDF renderer is busy. Try again in a moment.");
  }

  if (response.status === 422) {
    throw new DocumentPdfDownloadError("This document is currently too large to render as a PDF.");
  }

  if (!response.ok) {
    throw new DocumentPdfDownloadError(`The PDF could not be generated (server returned ${response.status}).`);
  }

  const contentType = response.headers.get("Content-Type")
    ?.split(";", 1)[0]
    ?.trim()
    .toLowerCase();
  if (contentType !== pdfContentType) {
    throw new DocumentPdfDownloadError("The server did not return a PDF. Your session may have expired.");
  }

  const content = await response.blob();
  if (await readPdfSignature(content) !== "%PDF-") {
    throw new DocumentPdfDownloadError("The server returned an invalid PDF. Nothing was downloaded.");
  }

  return {
    content,
    fileName: documentPdfFileName(response.headers.get("Content-Disposition"))
  };
}

export function documentPdfFileName(contentDisposition: string | null) {
  const encodedName = contentDisposition?.match(/filename\*\s*=\s*UTF-8''([^;]+)/iu)?.[1];
  if (encodedName) {
    try {
      return normalizePdfFileName(decodeURIComponent(stripQuotes(encodedName)));
    } catch {
      // Fall through to the plain filename parameter when the encoded value is malformed.
    }
  }

  const plainName = contentDisposition?.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/iu);
  return normalizePdfFileName(plainName?.[1] ?? plainName?.[2] ?? "qprins-document.pdf");
}

export async function runDocumentPdfDownload(
  trigger: HTMLButtonElement,
  url: string,
  services: DocumentPdfDownloadServices = {}) {
  const ownerDocument = services.document ?? document;
  const request = services.request ?? requestDocumentPdf;
  const save = services.save ?? saveDocumentPdf;
  const label = trigger.querySelector<HTMLElement>("[data-document-pdf-label]");
  const spinner = trigger.querySelector<HTMLElement>("[data-document-pdf-spinner]");
  const statusId = trigger.dataset.pdfStatusId;
  const status = statusId ? ownerDocument.getElementById(statusId) : null;
  const idleLabel = label?.textContent?.trim() || "Print PDF";

  trigger.disabled = true;
  trigger.setAttribute("aria-busy", "true");
  if (label) label.textContent = "Preparing PDF…";
  if (spinner) spinner.hidden = false;
  clearStatus(status);

  try {
    const file = await request(url);
    save(file);
    showStatus(status, "PDF ready. Download started.", false);
  } catch (error) {
    const message = error instanceof DocumentPdfDownloadError
      ? error.message
      : "The PDF could not be downloaded. Try again.";
    showStatus(status, message, true);
  } finally {
    trigger.disabled = false;
    trigger.removeAttribute("aria-busy");
    if (label) label.textContent = idleLabel;
    if (spinner) spinner.hidden = true;
  }
}

function saveDocumentPdf(file: DocumentPdfFile) {
  const objectUrl = URL.createObjectURL(file.content);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = file.fileName;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

async function readPdfSignature(content: Blob) {
  const bytes = await content.slice(0, 5).arrayBuffer();
  return new TextDecoder("ascii").decode(bytes);
}

function redirectedToLogin(response: Response) {
  if (!response.redirected || !response.url) return false;

  try {
    return new URL(response.url, window.location.href).pathname.toLowerCase() === "/account/login";
  } catch {
    return false;
  }
}

function normalizePdfFileName(value: string) {
  const normalized = stripQuotes(value)
    .replace(/[\\/]/gu, "-")
    .replace(/[\u0000-\u001f\u007f]/gu, "")
    .trim();
  if (!normalized) return "qprins-document.pdf";
  return normalized.toLowerCase().endsWith(".pdf") ? normalized : `${normalized}.pdf`;
}

function stripQuotes(value: string) {
  return value.trim().replace(/^"|"$/gu, "");
}

function clearStatus(status: HTMLElement | null) {
  if (!status) return;
  status.hidden = true;
  status.textContent = "";
  status.removeAttribute("data-state");
  status.setAttribute("role", "status");
}

function showStatus(status: HTMLElement | null, message: string, isError: boolean) {
  if (!status) return;
  status.textContent = message;
  status.dataset.state = isError ? "error" : "success";
  status.setAttribute("role", isError ? "alert" : "status");
  status.hidden = false;
}
